/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * "Verder met je vorige vergelijking": the Stap 1 card offers a returning
 * visitor the comparison they made last time (docs/plan-vergelijker-resultaten-
 * 2026-09-30.md section 12.4 B2; section 12.2, "Remembered inputs are offered,
 * never applied").
 *
 * Offered, never applied. One tap follows a link to the address that carries
 * the remembered inputs (compareMemory.ts `resumeHref`, ending in the
 * comparer's anchor), and the comparer re-derives everything from that address
 * exactly as it does for a shared link; the router's scroll manager
 * (ScrollToTop.tsx, lib/pageScroll.ts) brings the results into view under the
 * header. There is no scroll call here.
 *
 * When it shows:
 *   - only in the browser, after mount. Storage is the browser's and the bake
 *     has none, so a chip in the server HTML or the first client render would
 *     be a hydration mismatch against the baked card. State starts null and
 *     only an effect fills it (the FloatingCompareCta idiom);
 *   - only on an address without a comparison parameter
 *     (compareInputsFromUrl.ts). With one, the visitor is looking at a
 *     comparison already, and the card is remembering that one as they go. The
 *     address is watched, not read once: touching a filter hides the chip, and
 *     an address that is clean again reads the storage again, so the chip
 *     offers the comparison just made, never a stale one held in state;
 *   - only while there is something worth offering (readCompareMemory: written
 *     in the last 90 days, not the defaults) whose looptijd the card can name;
 *   - never again in this tab once dismissed (sessionStorage, compareMemory.ts).
 *
 * The label names the three inputs a visitor recognises, in the words the card
 * itself prints: the volume formatted for the language, the audience as the
 * toggle names it and the looptijd as the dropdown names it (durationOptions),
 * both starting lower case mid-sentence; never the data identifiers
 * ('Zakelijk', '1 jaar'). The smart-charger and Voorwaarden filters travel in
 * the link without a word in the label.
 *
 * Events, never fired inside a state updater: compare_resume_offer when it
 * shows (a non-interaction in the site's own stream, so it never turns a bounce
 * into a session that did something — server/data/siteEvents.ts),
 * compare_resume_click and compare_resume_dismiss; with `audience` and
 * `contract_duration` (both on the first-party allowlist, src/lib/gtag.ts) and
 * the hero surface the card sits in.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { History, X } from 'lucide-react';
import { useLanguage, type Language } from '../../contexts/LanguageContext';
import {
  dismissResumeForSession,
  isResumeDismissed,
  readCompareMemory,
  resumeHref,
  type CompareInputs,
  type CompareMemory,
} from '../../lib/compareMemory';
import { hasCompareFilterParam } from '../../lib/compareInputsFromUrl';
import { getCompareAnchor } from '../../lib/compareAnchor';
import {
  durationOptions,
  durationSelectionLabel,
  parseDurationSelection,
} from '../../lib/durationOptions';
import { formatInteger } from '../../lib/formatters';
import { SURFACES, trackEvent } from '../../lib/gtag';
import { nextFocusable } from '../../lib/nextFocusable';
import { fillTemplate } from '../../lib/templateVars.js';

/** The card's audience toggle (Filters.tsx): the label key per data identifier. */
const AUDIENCE_LABEL_KEYS: Readonly<Record<string, string>> = {
  Particulier: 'filters.particulier',
  Zakelijk: 'filters.zakelijk',
  Alle: 'filters.all',
};

/**
 * A control's label as a word inside a sentence: "Zakelijk" → "zakelijk",
 * "Alle looptijden" → "alle looptijden". Only a capitalised word is lowered: an
 * acronym ("MKB") or a label that starts with a number ("1 jaar") stays as the
 * CMS wrote it.
 */
const midSentence = (label: string, language: Language): string => {
  const [first, second] = Array.from(label);
  if (!first || !second || second !== second.toLocaleLowerCase(language)) return label;
  return first.toLocaleLowerCase(language) + label.slice(first.length);
};

const NBSP = String.fromCharCode(0xa0);
/** A value that never breaks across two lines ("1 jaar", "alle looptijden"). */
const unbroken = (value: string): string => value.replace(/ /g, NBSP);

/**
 * The chip's text, or null when the card has no name for an input. The
 * looptijd is the only one that can lack one: a value typed into an address by
 * hand ('7 jaar') that no dropdown offers. Such a comparison is not offered at
 * all rather than shown under its identifier.
 *
 * The values hold together: no break inside a value, between the volume and
 * the word after it (the unit), or on either side of a " · " separator. A
 * narrow chip then wraps in the lead-in ("Verder met je vorige vergelijking:")
 * and never starts a line with a separator or splits "1 jaar".
 */
export function resumeChipLabel(
  memory: CompareInputs,
  language: Language,
  t: (key: string) => unknown
): string | null {
  const audienceKey = AUDIENCE_LABEL_KEYS[memory.audience];
  // With no rows the helper returns the full canonical list, so every looptijd
  // the dropdown could ever offer has its label here.
  const options = durationOptions(null, null, (key) => String(t(key)));
  // Every looptijd the remembered selection names must have a label (a
  // multi-select since 2026-10-16: 'Alle', one, or a comma list).
  const known = (parseDurationSelection(memory.contractDuration) ?? []).every((d) =>
    options.some((option) => option.value === d)
  );
  if (!audienceKey || !known) return null;
  const duration = {
    label: durationSelectionLabel(
      memory.contractDuration,
      options,
      String(t('filters.all_durations')),
      (count) =>
        fillTemplate(String(t('filters.durations_count')), {
          count: formatInteger(count, language),
        })
    ),
  };
  const volume = formatInteger(memory.volume, language);
  return fillTemplate(String(t('filters.resume_chip')), {
    volume,
    audience: unbroken(midSentence(String(t(audienceKey)), language)),
    duration: unbroken(midSentence(duration.label, language)),
  })
    .replace(`${volume} `, `${volume}${NBSP}`)
    .replace(/ ?· ?/g, (separator) => separator.replace(/ /g, NBSP));
}

export const ResumeCompareChip: React.FC = () => {
  const { t, language } = useLanguage();
  const { search } = useLocation();
  const addressIsComparison = hasCompareFilterParam(new URLSearchParams(search));
  const [memory, setMemory] = useState<CompareMemory | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  /** The stamp of the entry last announced, so one offer is one event (StrictMode re-runs effects). */
  const offeredAt = useRef<number | null>(null);

  // Read the storage after mount, and again whenever the address becomes clean
  // (the comparison the visitor just made may have replaced the old one). An
  // address that carries a comparison hides the chip.
  useEffect(() => {
    if (addressIsComparison || isResumeDismissed()) {
      setMemory(null);
      return;
    }
    const remembered = readCompareMemory();
    if (!remembered || resumeChipLabel(remembered, language, t) === null) {
      setMemory(null);
      return;
    }
    setMemory(remembered);
    if (offeredAt.current === remembered.at) return;
    offeredAt.current = remembered.at;
    trackEvent('compare_resume_offer', {
      audience: remembered.audience,
      contract_duration: remembered.contractDuration,
      surface: SURFACES.hero,
      language,
    });
    // Only the address decides when the storage is read again. `t` and
    // `language` shape the label, which is computed at render from the current
    // ones, so a language switch relabels the chip without a second offer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressIsComparison]);

  const label = memory ? resumeChipLabel(memory, language, t) : null;
  if (!memory || !label) return null;

  const eventParams = {
    audience: memory.audience,
    contract_duration: memory.contractDuration,
    surface: SURFACES.hero,
    language,
  };

  const dismiss = () => {
    trackEvent('compare_resume_dismiss', eventParams);
    dismissResumeForSession();
    // The button that had focus is about to disappear: hand focus on to the
    // card's next control instead of letting it fall back to the page.
    const root = rootRef.current;
    if (root && root.contains(document.activeElement)) nextFocusable(root)?.focus();
    setMemory(null);
  };

  return (
    <div
      ref={rootRef}
      data-resume-chip
      className="mb-5 md:mb-6 flex items-stretch rounded-2xl lg:rounded-full border border-emerald-500/30 bg-emerald-900/30 [html.light_&]:bg-[#ecfdf5]"
    >
      <Link
        to={resumeHref(memory, language, getCompareAnchor(language))}
        onClick={() => trackEvent('compare_resume_click', eventParams)}
        className="flex-1 min-w-0 min-h-11 lg:min-h-9 flex items-center gap-2 pl-3 pr-1 py-1.5 rounded-2xl lg:rounded-full text-xs leading-4 font-bold text-emerald-300 [html.light_&]:text-[#065f46] text-pretty hover:text-emerald-200 [html.light_&]:hover:text-[#064e3b] hover:underline underline-offset-2 focus-ring"
      >
        <History className="w-4 h-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0">{label}</span>
      </Link>
      <button
        type="button"
        onClick={dismiss}
        aria-label={String(t('filters.resume_dismiss_aria'))}
        className="shrink-0 w-11 lg:w-9 min-h-11 lg:min-h-9 flex items-center justify-center rounded-2xl lg:rounded-full text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 focus-ring transition-colors"
      >
        <X className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
};
